// src/services/apiClient.ts
import { API_CONFIG } from "../utils/constants";

interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

interface ApiError extends Error {
  status?: number;
  errors?: Record<string, string[]>;
  response?: any;
}

class ApiClient {
  private baseURL: string;
  private timeout: number;
  private headers: Record<string, string>;

  constructor(baseURL?: string, timeout: number = 1000000) {
    this.baseURL = baseURL || API_CONFIG.BASE_URL;
    this.timeout = timeout;
    this.headers = {
      'Content-Type': 'application/json',
      // 'Content-Type': 'multipart/form-data',
    };
  }

  /**
   * Set Authorization token manually
   */
  setAuthToken(token: string) {
    this.headers['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Remove Authorization token
   */
  removeAuthToken() {
    delete this.headers['Authorization'];
  }

  /**
   * Attach token from localStorage automatically
   */
  private attachToken() {
    const token = localStorage.getItem('accessToken');
    if (token) {
      this.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.headers['Authorization'];
    }
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): never {
    const apiError: ApiError = new Error(error.message || "An unknown error occurred") as ApiError;
    apiError.status = error.status;
    apiError.errors = error.errors;
    apiError.response = error.response;
    throw apiError;
  }

  /**
   * Generic request
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    this.attachToken(); // attach token for every request
    const url = `${this.baseURL}${endpoint}`;

    const config: RequestInit = {
      ...options,
      headers: {
        ...this.headers,
        ...options.headers,
      },
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, { ...config, signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw { message: response.statusText, status: response.status, response: errorData };
      }

      const data = await response.json();
      return { data, status: response.status, message: data.message };
    } catch (err: any) {
      this.handleError(err);
    }
  }

  /** GET request */
  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const queryString = params
      ? '?' + new URLSearchParams(params as any).toString()
      : '';
    return this.request<T>(`${endpoint}${queryString}`, { method: 'GET' });
  }

  /** POST request */
  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body: JSON.stringify(data) });
  } 

  /** PUT request */
  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body: JSON.stringify(data) });
  }

  /** PATCH request */
  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body: JSON.stringify(data) });
  }

  /** DELETE request */
  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Singleton instance
export const apiClient = new ApiClient();
export default apiClient;
